import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

interface NavItem {
  label: string;
  icon: string;
  href: string;
  /** Padrão de URL pra marcar ativo (startsWith). */
  matchPrefix: string;
}

const ITEMS: ReadonlyArray<NavItem> = [
  { label: 'Home', icon: '🏠', href: '/app', matchPrefix: '/app' },
  { label: 'Grupos', icon: '👥', href: '/app/ligas', matchPrefix: '/app/ligas' },
  { label: 'Apostas', icon: '🎯', href: '/app/apostas', matchPrefix: '/app/apostas' },
  { label: 'Perfil', icon: '👤', href: '/app/perfil', matchPrefix: '/app/perfil' },
];

/**
 * Bottom nav fixa pro app autenticado. Visível só em rotas `/app/*`,
 * escondida em `/auth/*`, `/onboarding`, `/i/*`.
 *
 * Active state: o item cuja `matchPrefix` é o mais longo prefix da
 * URL atual (ordem importa — `/app/ligas` antes de `/app`).
 *
 * Mobile-first com safe-area-inset-bottom pra notch iOS.
 */
@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  template: `
    @if (visivel()) {
      <nav
        class="fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800"
        style="padding-bottom: env(safe-area-inset-bottom);"
        aria-label="Navegação principal"
      >
        <ul class="flex items-stretch max-w-md mx-auto">
          @for (item of items; track item.href) {
            <li class="flex-1">
              <a
                [routerLink]="item.href"
                [attr.aria-current]="ativo() === item.matchPrefix ? 'page' : null"
                class="flex flex-col items-center justify-center py-2 min-h-[56px] gap-0.5"
                [class.text-brand-500]="ativo() === item.matchPrefix"
                [class.text-neutral-500]="ativo() !== item.matchPrefix"
                [class.dark:text-neutral-400]="ativo() !== item.matchPrefix"
              >
                <span class="text-xl leading-none" aria-hidden="true">{{ item.icon }}</span>
                <span class="text-[11px] font-medium">{{ item.label }}</span>
              </a>
            </li>
          }
        </ul>
      </nav>
    }
  `,
})
export class BottomNavComponent {
  protected readonly items = ITEMS;
  private readonly router = inject(Router);

  constructor() {
    // Toggle classe `has-bottom-nav` no <body> baseado na visibilidade.
    // styles.css usa essa classe pra dar padding-bottom suficiente
    // (altura da nav + safe-area) e evitar que o conteúdo final fique
    // escondido atrás da nav.
    effect(() => {
      if (typeof document === 'undefined') return;
      document.body.classList.toggle('has-bottom-nav', this.visivel());
    });
  }

  /** URL atual (signal). */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** Visível só em rotas autenticadas do app. */
  readonly visivel = computed(() => {
    const u = this.url();
    return u.startsWith('/app');
  });

  /**
   * Item ativo — pega o `matchPrefix` mais longo que bate com a URL.
   * Ex: URL=/app/ligas/123 → matchPrefix=/app/ligas (não /app).
   */
  readonly ativo = computed(() => {
    const u = this.url();
    let best: string | null = null;
    for (const item of this.items) {
      if (u.startsWith(item.matchPrefix)) {
        if (!best || item.matchPrefix.length > best.length) {
          best = item.matchPrefix;
        }
      }
    }
    return best;
  });
}
